/**
 * InvEstablishExplainer — progressive-disclosure explainer for INV.ESTABLISH (I1).
 *
 * INV.ESTABLISH is the F-algebra side of the INV.CONSISTENCY dual pair. It
 * neutralizes the vulnerability class "invariant established unsoundly" by
 * making the unsound builder structurally inexpressible: no closed term in
 * the kernel category names a non-isomorphic α on the initial algebra.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React from "react";
import { INV_ESTABLISH_META } from "@csk/core";
import { THEOREM_COLOR, withAlpha } from "../../design-system/tokens.js";
import { MathInline, tex } from "../../components/math/index.js";
import {
  ExplainerCard,
  FAlgebraDiagram,
  type ExplainerCrossLink,
  type ExplainerIdentity,
  type ExplainerTierContent,
} from "../shared/index.js";
import { TheoremMetaStrip } from "./TheoremMetaStrip.js";
import { TIER_ONE_IMMUNITY_LINK } from "./tierOneCrossLinks.js";

const ACCENT = THEOREM_COLOR["INV.ESTABLISH"];

const IDENTITY: ExplainerIdentity = {
  id: "explainer-inv-establish",
  kind: "SSM · Neutralization Theorem (S1)",
  title: "INV.ESTABLISH",
  subtitle: "I1 · F-algebra · INERT · G3 Time",
  accent: ACCENT,
};

const CROSS_LINKS: readonly ExplainerCrossLink[] = [
  { label: "F-Algebra (structure)", href: "#explainer-falgebra", accent: ACCENT },
  {
    label: "INV.PRESERVE (dual)",
    href: "#explainer-inv-preserve",
    accent: THEOREM_COLOR["INV.PRESERVE"],
  },
  { label: "INV.CONSISTENCY (DPG)", href: "#explainer-inv-consistency" },
  { label: "ALWAYS (SDP)", href: "#explainer-always" },
  TIER_ONE_IMMUNITY_LINK,
];

const TIERS: ExplainerTierContent = {
  tldr: (
    <p>
      <strong>INV.ESTABLISH</strong> says: when CSK constructs a value, the invariant for that value
      holds — by construction, not by a runtime check. There is no other way to construct it.
    </p>
  ),
  intuition: (
    <>
      <p>
        Most systems have an "establish" event that asserts an invariant — for example, "after this
        transaction, the ledger balances." The usual failure mode is that the establish event is
        itself defective: it claims the invariant but the data does not satisfy it.
      </p>
      <p>
        CSK eliminates this failure mode by typing every constructor as an F-algebra. The
        constructor cannot run unless its inputs satisfy the algebra's structure map; therefore the
        only inhabitants of the carrier are the ones whose invariant holds. The unsound builder is{" "}
        not "rejected at runtime" — it is unconstructable in the source.
      </p>
    </>
  ),
  formal: (
    <>
      <p>
        Let <MathInline>{tex`F`}</MathInline> be the invariant functor for a domain (e.g., "either
        an empty ledger, or a ledger plus a balanced transaction"). The kernel exposes only the
        initial F-algebra <MathInline>{tex`\mu F`}</MathInline> together with its structure map{" "}
        <MathInline>{tex`\alpha : F(\mu F) \to \mu F`}</MathInline>.
      </p>
      <p style={{ marginTop: "0.5rem" }}>
        Lambek's lemma makes <MathInline>{tex`\alpha`}</MathInline> an isomorphism. The carrier{" "}
        <MathInline>{tex`\mu F`}</MathInline> is therefore exactly its own unfolding — there is no
        "extra state" hiding an unsound establishment. Every term of type{" "}
        <MathInline>{tex`\mu F`}</MathInline> is a finite folding through{" "}
        <MathInline>{tex`\alpha`}</MathInline> of <MathInline>{tex`F`}</MathInline>-shaped data.
      </p>
      <p>
        Soundness is then the universal property of the catamorphism: for any invariant predicate{" "}
        <MathInline>{tex`P`}</MathInline> closed under <MathInline>{tex`F`}</MathInline>,{" "}
        <MathInline>{tex`\mathrm{cata}\,\beta : \mu F \to \Sigma\, x.\, P\, x`}</MathInline> is the
        unique algebra homomorphism into the truth-set. There is no second route to{" "}
        <MathInline>{tex`\mu F`}</MathInline>.
      </p>
    </>
  ),
  relevance: (
    <>
      <p>
        <strong>Vulnerability class neutralized:</strong> "invariant asserted at construction does
        not actually hold" — the unsound establish event. CSK realizes this by{" "}
        <em>structural inexpressibility</em>: the unsound builder is not a closed term in the kernel
        category.
      </p>
      <p>
        <strong>Theorem-collision check.</strong> If you bypass INV.ESTABLISH (e.g., construct a
        state by parsing untrusted bytes into the carrier), you have stopped using{" "}
        <MathInline>{tex`\mu F`}</MathInline> and instead used a different carrier whose initial
        algebra you have not certified. INV.ESTABLISH is then vacuous on that carrier — the check
        has gone away, not been bypassed.
      </p>
      <p>
        <strong>Seam type</strong> (per <code>theory/structural/SEAM-THEORY.md</code>): vertical.{" "}
        <strong>Bridge</strong>: validity override (governance artifact).
      </p>
    </>
  ),
  references: [
    {
      label: "INV.ESTABLISH theorem card",
      path: "docs/reference/theorem-cards/INV-ESTABLISH.md",
    },
    {
      label: "Kernel module",
      path: `agda/${INV_ESTABLISH_META.agdaSource}`,
      note: `INV_ESTABLISH_META.agdaSource is "${INV_ESTABLISH_META.agdaSource}"; the repo path is prefixed with "agda/".`,
    },
    {
      label: "F-algebra base module",
      path: "agda/Kernel/INV/ESTABLISH/Base.agda",
    },
    {
      label: "Manuscript chapter (PR #1544)",
      path: "https://github.com/InvariantHoldings/categorical-safety-kernel/pull/1544",
      note: "Chapter 02 (F-Algebras / Invariant Establishment) lands with PR #1544's Structural Invariant Safety Guarantees Contrib manuscript.",
    },
    {
      label: "Existing demo",
      path: "demos/app/src/demos/InvariantLifecycle.tsx",
      note: "Invariant Lifecycle Governance — the temporal-persistence story; complementary to this structural one.",
    },
  ],
};

const DIAGRAM = (
  <>
    <TheoremMetaStrip
      theoremName="INV.ESTABLISH"
      code="I1"
      structure="F-algebra"
      mode="inert"
      generator="G3"
      seamType={INV_ESTABLISH_META.seamType}
      bridgeArtifact="validity override"
      theoremColor={ACCENT}
      washColor={withAlpha(ACCENT, "1A")}
    />
    <div style={{ marginTop: "0.5rem" }}>
      <FAlgebraDiagram accent={ACCENT} />
    </div>
  </>
);

export function InvEstablishExplainer(): React.JSX.Element {
  return (
    <ExplainerCard identity={IDENTITY} tiers={TIERS} diagram={DIAGRAM} crossLinks={CROSS_LINKS} />
  );
}
